import { renderButton } from 'BomdisoftComponents/buttons/render';
import { renderBottomSheet } from 'BomdisoftComponents/drawers/render';
import { attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { renderTextField } from 'BomdisoftComponents/inputs/field/render';
import { cancel, type SupportedLanguage } from '@bomdisoft/translations';

export type DialogSize = 'compact' | 'default' | 'wide';

/** Deeper than this and every further dialog shares the top rung. Three live
 *  layers is already past what any screen here opens, and an unbounded ladder
 *  would need an unbounded set of tokens to climb. */
export const DIALOG_MAX_DEPTH = 2;

export const toDialogDepth = (depth: number) => Math.min(Math.max(depth, 0), DIALOG_MAX_DEPTH);

export type DialogOptions = {
  children?: HtmlValue;
  className?: string;
  depth?: number;
  draggable?: boolean;
  form?: boolean;
  isSheet?: boolean;
  modal?: boolean;
  size?: DialogSize;
  testId?: string;
  titleId: string;
};

export const renderDialog = ({ children, className, depth = -1, draggable = true, form = false, isSheet = false, modal = true, size = 'default', testId, titleId }: DialogOptions) =>
  isSheet && form
    ? renderBottomSheet({ children, className, flush: true, labelledBy: titleId, testId })
    : html`<dialog${attributes({ 'aria-labelledby': titleId, class: joinClasses('bomdisoft-dialog', className), 'data-depth': String(toDialogDepth(depth)), 'data-draggable': draggable, 'data-form': form, 'data-modal': modal ? undefined : 'false', 'data-size': size, 'data-testid': testId })}>${children}</dialog>`;

const GRIP = html`<svg aria-hidden="true" class="bomdisoft-dialog-grip" fill="currentColor" height="12" viewBox="0 0 5 12" width="5"><rect height="12" rx="0.9" width="1.8" x="0" y="0"></rect><rect height="12" rx="0.9" width="1.8" x="3.2" y="0"></rect></svg>`;

export const renderDialogTitle = ({ children, className, titleId }: { children?: HtmlValue; className?: string; titleId: string }) => html`<h2${attributes({ class: joinClasses('bomdisoft-dialog-title', className), id: titleId })}>${GRIP}${children}</h2>`;

export const renderDialogContent = ({ children, className }: { children?: HtmlValue; className?: string }) => html`<div${attributes({ class: joinClasses('bomdisoft-dialog-content', className) })}>${children}</div>`;

export const renderDialogActions = ({ children, className }: { children?: HtmlValue; className?: string }) => html`<div${attributes({ class: joinClasses('bomdisoft-dialog-actions', className) })}>${children}</div>`;

const renderDecisionActions = ({ cancelLabel, confirmDisabled, confirmLabel, destructive, language, testId }: { cancelLabel?: HtmlValue | false; confirmDisabled: boolean; confirmLabel: HtmlValue; destructive?: boolean; language: SupportedLanguage; testId?: string }) =>
  renderDialogActions({
    children: html`${cancelLabel === false ? '' : renderButton({ attributes: { 'data-testid': testId ? `${testId}-cancel` : undefined }, children: cancelLabel ?? cancel[language], color: 'neutral', variant: 'text' })}${renderButton({ attributes: { 'data-testid': testId ? `${testId}-confirm` : undefined, disabled: confirmDisabled }, children: confirmLabel, color: destructive ? 'error' : undefined, variant: 'contained' })}`,
  });

export type ConfirmDialogOptions = {
  cancelLabel?: HtmlValue | false;
  confirmLabel: HtmlValue;
  confirmPhrase?: string;
  confirmPhraseLabel?: string;
  description?: HtmlValue;
  destructive?: boolean;
  fieldId: string;
  icon?: HtmlValue;
  isSheet?: boolean;
  language: SupportedLanguage;
  pending?: boolean;
  testId?: string;
  title: HtmlValue;
  titleId: string;
  typed?: string;
};

export const isConfirmPhraseUnmatched = (confirmPhrase: string | undefined, typed: string) => confirmPhrase !== undefined && typed.trim().toLowerCase() !== confirmPhrase.trim().toLowerCase();

export const renderConfirmDialog = ({ cancelLabel, confirmLabel, confirmPhrase, confirmPhraseLabel, description, destructive, fieldId, icon, isSheet, language, pending, testId, title, titleId, typed = '' }: ConfirmDialogOptions) =>
  renderDialog({
    children: html`${renderDialogTitle({ children: icon === undefined ? title : html`<span class="bomdisoft-confirm-dialog-head-row"><span class="bomdisoft-confirm-dialog-glyph">${icon}</span>${title}</span>`, titleId })}${renderDialogContent({
      children: html`${description === undefined ? '' : html`<div class="bomdisoft-confirm-dialog-description">${description}</div>`}${confirmPhrase === undefined ? '' : renderTextField({ attributes: { autofocus: true, placeholder: confirmPhrase }, id: fieldId, label: confirmPhraseLabel, type: 'text', value: typed })}`,
    })}${renderDecisionActions({ cancelLabel, confirmDisabled: isConfirmPhraseUnmatched(confirmPhrase, typed) || pending === true, confirmLabel, destructive, language, testId })}`,
    form: confirmPhrase !== undefined,
    isSheet,
    testId,
    titleId,
  });

export type PromptDialogOptions = {
  cancelLabel?: HtmlValue | false;
  confirmLabel: HtmlValue;
  description?: HtmlValue;
  fieldId: string;
  helperId?: string;
  isSheet?: boolean;
  label: string;
  language: SupportedLanguage;
  message?: string | null;
  pending?: boolean;
  placeholder?: string;
  size?: DialogSize;
  testId?: string;
  title: HtmlValue;
  titleId: string;
  value?: string;
};

export const isPromptBlocked = (value: string, message: string | null | undefined, pending: boolean | undefined) => value.trim() === '' || (message ?? null) !== null || pending === true;

export const renderPromptDialog = ({ cancelLabel, confirmLabel, description, fieldId, helperId = `${fieldId}-helper`, isSheet, label, language, message = null, pending, placeholder, size = 'compact', testId, title, titleId, value = '' }: PromptDialogOptions) =>
  renderDialog({
    children: html`${renderDialogTitle({ children: title, titleId })}${renderDialogContent({
      children: html`${description === undefined ? '' : html`<p class="bomdisoft-prompt-dialog-description">${description}</p>`}${renderTextField({ attributes: { autofocus: true, placeholder }, error: message !== null, helperId, helperText: message ?? undefined, id: fieldId, label, type: 'text', value })}`,
    })}${renderDecisionActions({ cancelLabel, confirmDisabled: isPromptBlocked(value, message, pending), confirmLabel, language, testId })}`,
    form: true,
    isSheet,
    size,
    testId,
    titleId,
  });
